import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@components/ui/Select";
import { listQuestionsStart } from "@shared/constants/appConstants";
import { routes } from "@shared/constants/routes";
import Button from "@components/ui/Button";
import VStack from "@components/layout/VStack";
import DivShadow from "@components/layout/DivShadow";
import { useOutlookStore, useSettingStore, useThemeStore } from "@shared/stores";
import SelectLocationForm from "@features/search/components/SelectLocationForm";

import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { OrganizerSettingEntity } from "@features/settings/services/entities/organizerSettingEntity";
import { ResourceTypeEntity } from "@features/search/services/entities/resourceTypeEntity";
import { useAppConfigStore } from "@shared/stores/appConfigStore";
import { useTranslation } from "react-i18next";
import { CityIcon, LocationIcon } from "../../components/icons/IconCustom";

const Start = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { isDarkMode } = useThemeStore();
  const { resourceTypes } = useAppConfigStore();
  const { updateDefaultLocations, updateDefaultResourceType, defaultLocations } = useSettingStore();
  // const queryClient = useQueryClient();

  const [locations, setLocations] = useState<number[]>([]);
  const [placeholder, setPlaceholder] = useState<string>("");
  const [resourceType, setResourceType] = useState<string>("");

  const resourceTypeOptions = useMemo(() => {
    return (
      (resourceTypes || [])?.map((i: ResourceTypeEntity) => ({
        label: i.displayName,
        value: i.type,
      })) || []
    );
  }, [resourceTypes]);

  const { mutate: updateDefaultLocationsMutation, isPending } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        updateDefaultLocations(data.defaultLocations);
        updateDefaultResourceType(data.defaultResourceType);
        navigate(routes.recommendation);
        // queryClient.setQueryData([QUERY_KEY.GET_ORGANIZER_SETTING, organizer], (oldData: OrganizerSettingEntity) => {
        //   return {
        //     ...oldData,
        //     defaultLocations: data.defaultLocations,
        //     defaultResourceType: data.defaultResourceType,
        //   };
        // });
      },
      onError: () => {
        // showToast(t("Failed to update default locations"), false);
      },
    },
  });

  const handleNext = () => {
    if (!locations.length || !resourceType) return;
    const defaultResourceTypes = defaultLocations.defaultResourceTypes.map((i) => ({
      type: i.type,
      locationIds: String(locations.join(",")),
    }));
    updateDefaultLocationsMutation({
      organizerEmail: organizer,
      defaultLocations: {
        defaultLocationIds: locations.join(","),
        defaultResourceTypes: defaultResourceTypes,
      },
      defaultResourceType: resourceType,
    });
  };

  const handleSingleSelect = (nodeIds: number[], placeHolder: string) => {
    setLocations(nodeIds);
    setPlaceholder(placeHolder);
  };

  const handleClear = () => {
    setLocations([]);
    setPlaceholder("");
  };

  const LocationFormComponent = useCallback(
    (props: any) => (
      <SelectLocationForm
        {...props}
        iShowFooter={false}
        locationSelected={[location]}
        onApply={handleSingleSelect}
        onClear={handleClear}
        isStartPage={true}
      />
    ),
    [handleSingleSelect, handleClear]
  );

  return (
    <div className="w-full h-screen bg-veryLightGray dark:bg-darkBackgroundColor dark:text-white">
      <VStack className="h-full" justifyContent="between">
        <VStack className="p-5" gap={5}>
          <div className="w-full mb-5">
            <ContentCommon title={t(listQuestionsStart[0].title)} description={t(listQuestionsStart[0].description)} />
            <Select
              placeholder={placeholder || t("Location")}
              className="w-full text-sm h-[32px]"
              icon={<LocationIcon isDarkMode={isDarkMode} isActive={Boolean(locations?.length)} />}
              sizeArrowDown="w-6 h-6"
              Children={LocationFormComponent}
              isHighlightSelected={Boolean(locations?.length)}
            />
          </div>
          <div className="w-full">
            <ContentCommon title={t(listQuestionsStart[1].title)} description={t(listQuestionsStart[1].description)} />
            <Select
              placeholder={t("Resource Type")}
              className="w-full text-sm h-[32px] py-2"
              icon={<CityIcon isDarkMode={isDarkMode} isActive={Boolean(resourceType)} />}
              options={resourceTypeOptions}
              value={resourceType}
              onChange={(value) => setResourceType(value as string)}
              sizeArrowDown="w-6 h-6"
              isHighlightSelected={Boolean(resourceType)}
            />
          </div>
        </VStack>

        <DivShadow className="p-4 bg-veryLightGray dark:bg-charcoalGray">
          <Button
            label={t("Next")}
            isDefault={true}
            className="text-sm font-semibold py-2 w-full"
            onClick={handleNext}
            disabled={!location || !resourceType || isPending}
          />
        </DivShadow>
      </VStack>
    </div>
  );
};

export default Start;

const ContentCommon = ({ title, description }: { title: string; description: string }) => {
  return (
    <div>
      <p className="font-semibold text-[21px] mb-2 text-dark">{title}</p>
      <p className="text-sm font-normal text-darkerGray mb-5 dark:text-softGray">{description}</p>
    </div>
  );
};
